import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Paper,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Event as EventIcon,
  CalendarToday as CalendarIcon,
  RemoveCircle as RemoveIcon,
  Circle as CircleIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { getAuthHeaders } from '../../utils/auth';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { pl } from 'date-fns/locale';
import { format, parseISO, addDays } from 'date-fns';

const EventManagement = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [createEditDialogOpen, setCreateEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addLessonDialogOpen, setAddLessonDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [viewingEvent, setViewingEvent] = useState(null);
  const [deletingEvent, setDeletingEvent] = useState(null);
  const [availableLessons, setAvailableLessons] = useState([]);
  const [loadingLessons, setLoadingLessons] = useState(false);
  const [lessonDateRange, setLessonDateRange] = useState({
    start: new Date(),
    end: addDays(new Date(), 30),
  });
  const [lessonTypes, setLessonTypes] = useState([]);
  const [selectedLessonType, setSelectedLessonType] = useState('');

  // Predefined color options for events
  const EVENT_COLORS = [
    { value: '#ff9800', label: 'Pomarańczowy' },
    { value: '#4caf50', label: 'Zielony' },
    { value: '#2196f3', label: 'Niebieski' },
    { value: '#9c27b0', label: 'Fioletowy' },
    { value: '#e91e63', label: 'Różowy' },
    { value: '#f44336', label: 'Czerwony' },
    { value: '#00bcd4', label: 'Cyjan' },
    { value: '#795548', label: 'Brązowy' },
    { value: '#607d8b', label: 'Szary' },
    { value: '#ff5722', label: 'Głęboki pomarańczowy' },
  ];

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#ff9800',
    active: true,
    show_commercial: false,
    // All-day events carry their own date range instead of deriving it from
    // lessons, so they can occupy whole days in the grafik with nothing inside.
    all_day: false,
    all_day_start: null,
    all_day_end: null,
    show_in_timetable: true,
  });

  useEffect(() => {
    fetchEvents();
    fetchLessonTypes();
  }, []);

  useEffect(() => {
    if (addLessonDialogOpen) {
      fetchLessonsForDateRange();
    }
  }, [lessonDateRange, addLessonDialogOpen]);

  const fetchLessonTypes = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/lesson-type/`, { headers });
      if (response.ok) {
        const data = await response.json();
        // Handle both response formats: direct array or { objects: [...] }
        setLessonTypes(data.objects || data || []);
      }
    } catch (err) {
      console.error('Error fetching lesson types:', err);
    }
  };

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/admin/events`, { headers });
      if (response.ok) {
        const data = await response.json();
        setEvents(data || []);
        setError('');
      } else {
        setError(response.status === 403 || response.status === 401 
          ? 'Brak uprawnień do pobrania eventów' 
          : 'Nie udało się pobrać eventów');
      }
    } catch (err) {
      setError('Błąd połączenia z serwerem');
    } finally {
      setLoading(false);
    }
  };

  const fetchEventDetails = async (eventId) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/admin/events/${eventId}`, { headers });
      if (response.ok) {
        return await response.json();
      }
    } catch (err) {
      console.error('Error fetching event details:', err);
    }
    return null;
  };

  const fetchLessonsForDateRange = async () => {
    setLoadingLessons(true);
    try {
      const headers = await getAuthHeaders();
      const startDate = format(lessonDateRange.start, 'yyyy-MM-dd');
      const endDate = format(lessonDateRange.end, 'yyyy-MM-dd');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/lesson/by-date-range?start_date=${startDate}&end_date=${endDate}`,
        { headers }
      );
      if (response.ok) {
        const data = await response.json();
        // Filter out lessons that are already assigned to an event
        const lessonsWithoutEvent = (data.objects || []).filter(
          lesson => !lesson.event_id || lesson.event_id === viewingEvent?.id
        );
        setAvailableLessons(lessonsWithoutEvent);
      } else {
        setAvailableLessons([]);
      }
    } catch (err) {
      setAvailableLessons([]);
    } finally {
      setLoadingLessons(false);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      color: '#ff9800',
      active: true,
      show_commercial: false,
      all_day: false,
      all_day_start: null,
      all_day_end: null,
      show_in_timetable: true,
    });
  };

  const handleCreate = () => {
    resetForm();
    setEditingEvent(null);
    setCreateEditDialogOpen(true);
  };

  const handleEdit = (event) => {
    setFormData({
      name: event.name || '',
      description: event.description || '',
      color: event.color || '#ff9800',
      active: event.active ?? true,
      show_commercial: event.show_commercial ?? false,
      all_day: event.all_day ?? false,
      all_day_start: event.all_day_start ? parseISO(event.all_day_start) : null,
      all_day_end: event.all_day_end ? parseISO(event.all_day_end) : null,
      show_in_timetable: event.show_in_timetable ?? true,
    });
    setEditingEvent(event);
    setCreateEditDialogOpen(true);
  };

  const handleView = async (event) => {
    const eventDetails = await fetchEventDetails(event.id);
    if (eventDetails) {
      setViewingEvent(eventDetails);
      setViewDialogOpen(true);
    }
  };

  const handleDelete = (event) => {
    setDeletingEvent(event);
    setDeleteDialogOpen(true);
  };

  const handleOpenAddLessonDialog = () => {
    // Set date range - use event's date range if available, otherwise default to today + 30 days
    const startDate = viewingEvent?.start_date ? parseISO(viewingEvent.start_date) : new Date();
    const endDate = viewingEvent?.end_date ? parseISO(viewingEvent.end_date) : addDays(new Date(), 30);
    setLessonDateRange({ start: startDate, end: endDate });
    setSelectedLessonType('');
    setAddLessonDialogOpen(true);
  };

  // Helper function to format validation errors
  const formatValidationError = (errorData) => {
    if (typeof errorData.detail === 'string') {
      return errorData.detail;
    }
    if (Array.isArray(errorData.detail)) {
      return errorData.detail.map(err => {
        const field = err.loc?.slice(1).join('.') || 'pole';
        return `${field}: ${err.msg}`;
      }).join(', ');
    }
    return 'Błąd podczas zapisywania eventu';
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Nazwa eventu jest wymagana');
      return;
    }
    if (formData.all_day && !formData.all_day_start) {
      setError('Event całodniowy wymaga daty rozpoczęcia');
      return;
    }
    if (
      formData.all_day &&
      formData.all_day_end &&
      formData.all_day_end < formData.all_day_start
    ) {
      setError('Data zakończenia nie może być wcześniejsza niż data rozpoczęcia');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Send local midnight rather than an ISO/UTC timestamp: the backend snaps
      // the range to whole days, and a timezone shift would move the event to
      // the neighbouring day.
      const toLocalDayString = (value) =>
        value ? `${format(value, 'yyyy-MM-dd')}T00:00:00` : null;

      const apiData = {
        name: formData.name,
        description: formData.description || null,
        color: formData.color,
        active: formData.active,
        show_commercial: formData.show_commercial,
        all_day: formData.all_day,
        all_day_start: formData.all_day ? toLocalDayString(formData.all_day_start) : null,
        all_day_end: formData.all_day
          ? toLocalDayString(formData.all_day_end || formData.all_day_start)
          : null,
        show_in_timetable: formData.show_in_timetable,
      };

      const isEditing = editingEvent !== null;
      const url = isEditing
        ? `${process.env.REACT_APP_BACKEND_ADDRESS}/admin/events/${editingEvent.id}`
        : `${process.env.REACT_APP_BACKEND_ADDRESS}/admin/events`;

      const headers = await getAuthHeaders();
      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(apiData),
      });

      if (response.ok) {
        setSuccess(isEditing ? 'Event zaktualizowany pomyślnie' : 'Event utworzony pomyślnie');
        setCreateEditDialogOpen(false);
        await fetchEvents();
      } else {
        const errorData = await response.json();
        setError(formatValidationError(errorData));
      }
    } catch (err) {
      setError('Błąd połączenia z serwerem');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingEvent) return;
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/admin/events/${deletingEvent.id}`,
        { method: 'DELETE', headers }
      );
      if (response.ok) {
        setSuccess('Event usunięty pomyślnie');
        setDeleteDialogOpen(false);
        await fetchEvents();
      } else {
        setError('Nie udało się usunąć eventu');
      }
    } catch (err) {
      setError('Błąd połączenia z serwerem');
    } finally {
      setLoading(false);
      setDeletingEvent(null);
    }
  };

  const handleAddLesson = async (lessonId) => {
    if (!viewingEvent) return;
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/admin/events/${viewingEvent.id}/lessons/${lessonId}`,
        { method: 'POST', headers }
      );
      if (response.ok) {
        setSuccess('Zajęcia dodane do eventu');
        const updatedEvent = await fetchEventDetails(viewingEvent.id);
        if (updatedEvent) setViewingEvent(updatedEvent);
        await fetchLessonsForDateRange();
      } else {
        setError('Nie udało się dodać zajęć');
      }
    } catch (err) {
      setError('Błąd połączenia z serwerem');
    }
  };

  const handleRemoveLesson = async (lessonId) => {
    if (!viewingEvent) return;
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/admin/events/${viewingEvent.id}/lessons/${lessonId}`,
        { method: 'DELETE', headers }
      );
      if (response.ok) {
        setSuccess('Zajęcia usunięte z eventu');
        const updatedEvent = await fetchEventDetails(viewingEvent.id);
        if (updatedEvent) setViewingEvent(updatedEvent);
      } else {
        setError('Nie udało się usunąć zajęć');
      }
    } catch (err) {
      setError('Błąd połączenia z serwerem');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(parseISO(dateStr), 'dd.MM.yyyy HH:mm', { locale: pl });
    } catch {
      return dateStr;
    }
  };

  const formatLessonDate = (lesson) => {
    if (lesson.start) {
      try {
        return format(parseISO(lesson.start), 'dd.MM.yyyy HH:mm', { locale: pl });
      } catch {
        return lesson.start;
      }
    }
    return 'Data nieznana';
  };

  // Helper function to get lesson type name by ID
  const getLessonTypeName = (lessonTypeId) => {
    const lessonType = lessonTypes.find(lt => lt.id === lessonTypeId);
    return lessonType?.friendly_name || lessonType?.name || null;
  };

  // Filter lessons by selected lesson type
  const filteredLessons = selectedLessonType
    ? availableLessons.filter(lesson => lesson.lesson_type_id === selectedLessonType)
    : availableLessons;

  const columns = [
    { field: 'name', headerName: 'Nazwa', flex: 1, minWidth: 200 },
    {
      field: 'color',
      headerName: 'Kolor',
      width: 70,
      renderCell: (params) => (
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            backgroundColor: params.value || '#ff9800',
            border: '2px solid rgba(0,0,0,0.1)',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          }}
          title={EVENT_COLORS.find(c => c.value === params.value)?.label || params.value}
        />
      )
    },
    { 
      field: 'start_date', 
      headerName: 'Data rozpoczęcia', 
      width: 150,
      renderCell: (params) => formatDate(params.value)
    },
    { 
      field: 'end_date', 
      headerName: 'Data zakończenia', 
      width: 150,
      renderCell: (params) => formatDate(params.value)
    },
    {
      field: 'all_day',
      headerName: 'Typ',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Całodniowy' : 'Z zajęciami'}
          color={params.value ? 'warning' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'lesson_count',
      headerName: 'Zajęcia',
      width: 100,
      renderCell: (params) => (
        params.row.all_day ? (
          <Chip label="—" size="small" variant="outlined" />
        ) : (
          <Chip
            label={params.value || 0}
            color={params.value > 0 ? 'primary' : 'default'}
            size="small"
          />
        )
      )
    },
    {
      field: 'active',
      headerName: 'Aktywny',
      width: 100,
      renderCell: (params) => (
        <Chip 
          label={params.value ? 'Tak' : 'Nie'} 
          color={params.value ? 'success' : 'error'} 
          size="small" 
        />
      )
    },
    {
      field: 'show_commercial',
      headerName: 'Publiczny',
      width: 100,
      renderCell: (params) => (
        <Chip 
          label={params.value ? 'Tak' : 'Nie'} 
          color={params.value ? 'info' : 'default'} 
          size="small" 
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Akcje',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton onClick={() => handleView(params.row)} size="small">
            <VisibilityIcon />
          </IconButton>
          <IconButton onClick={() => handleEdit(params.row)} size="small">
            <EditIcon />
          </IconButton>
          <IconButton onClick={() => handleDelete(params.row)} size="small" color="error">
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pl}>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              <EventIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Zarządzanie Eventami
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Tworzenie eventów i przypisywanie do nich zajęć. Eventy mogą być powiązane z karnetami eventowymi.
            </Typography>
          </Box>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />} 
            onClick={handleCreate}
            sx={{ textTransform: 'none' }}
          >
            Nowy Event
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        <Paper sx={{ height: 500, width: '100%' }}>
          <DataGrid
            rows={events}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[5, 10, 25]}
            loading={loading}
            disableSelectionOnClick
            sx={{ '& .MuiDataGrid-cell:hover': { backgroundColor: '#f5f5f5' } }}
          />
        </Paper>

        {/* Create/Edit Dialog */}
        <Dialog open={createEditDialogOpen} onClose={() => setCreateEditDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingEvent ? 'Edytuj Event' : 'Nowy Event'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="Nazwa eventu"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Opis"
                value={formData.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
                margin="normal"
                multiline
                rows={3}
              />
              
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Checkbox
                    checked={formData.all_day}
                    onChange={(e) => handleFormChange('all_day', e.target.checked)}
                  />
                }
                label="Event całodniowy (bez zajęć w środku)"
              />

              {formData.all_day ? (
                <>
                  <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
                    Event zajmie wybrane dni w całości i pojawi się jako pasek nad grafikiem. Nie
                    trzeba przypisywać do niego żadnych zajęć.
                  </Alert>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <DatePicker
                        label="Dzień rozpoczęcia"
                        value={formData.all_day_start}
                        onChange={(v) => handleFormChange('all_day_start', v)}
                        slotProps={{ textField: { fullWidth: true } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DatePicker
                        label="Dzień zakończenia"
                        value={formData.all_day_end}
                        onChange={(v) => handleFormChange('all_day_end', v)}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            helperText: 'Puste = event jednodniowy',
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                  <FormControlLabel
                    sx={{ mt: 1 }}
                    control={
                      <Checkbox
                        checked={formData.show_in_timetable}
                        onChange={(e) => handleFormChange('show_in_timetable', e.target.checked)}
                      />
                    }
                    label="Pokazuj w grafiku"
                  />
                </>
              ) : (
                <Alert severity="info" sx={{ mt: 2, mb: 1 }}>
                  Daty eventu (rozpoczęcia i zakończenia) są automatycznie obliczane na podstawie przypisanych zajęć. Najpierw utwórz event, a następnie dodaj do niego zajęcia.
                </Alert>
              )}

              {/* Color picker */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Kolor eventu (wyświetlany w grafiku)
                </Typography>
                
                {/* Custom color picker */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1, mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => handleFormChange('color', e.target.value)}
                      style={{
                        width: 50,
                        height: 40,
                        padding: 0,
                        border: '2px solid #ccc',
                        borderRadius: 8,
                        cursor: 'pointer',
                      }}
                      title="Wybierz dowolny kolor"
                    />
                    <TextField
                      size="small"
                      value={formData.color}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^#[0-9A-Fa-f]{0,6}$/.test(value) || value === '') {
                          handleFormChange('color', value || '#ff9800');
                        }
                      }}
                      placeholder="#ff9800"
                      sx={{ width: 100 }}
                      inputProps={{ maxLength: 7 }}
                    />
                  </Box>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      backgroundColor: formData.color,
                      border: '2px solid rgba(0,0,0,0.2)',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    }}
                    title="Podgląd koloru"
                  />
                </Box>

                {/* Quick select predefined colors */}
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Lub wybierz z gotowych kolorów:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {EVENT_COLORS.map((colorOption) => (
                    <Box
                      key={colorOption.value}
                      onClick={() => handleFormChange('color', colorOption.value)}
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        backgroundColor: colorOption.value,
                        cursor: 'pointer',
                        border: formData.color === colorOption.value 
                          ? '3px solid #333' 
                          : '2px solid transparent',
                        boxShadow: formData.color === colorOption.value 
                          ? '0 0 0 2px #fff, 0 0 0 4px #333' 
                          : '0 2px 4px rgba(0,0,0,0.2)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'scale(1.1)',
                          boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                        },
                      }}
                      title={colorOption.label}
                    />
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Wybrany kolor: {EVENT_COLORS.find(c => c.value === formData.color)?.label || formData.color}
                </Typography>
              </Box>

              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.active}
                      onChange={(e) => handleFormChange('active', e.target.checked)}
                    />
                  }
                  label="Event aktywny"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.show_commercial}
                      onChange={(e) => handleFormChange('show_commercial', e.target.checked)}
                    />
                  }
                  label="Pokazuj publicznie (w ofercie)"
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateEditDialogOpen(false)} disabled={loading}>
              Anuluj
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? 'Zapisywanie...' : (editingEvent ? 'Aktualizuj' : 'Utwórz')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* View/Manage Lessons Dialog */}
        <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h6">
                <EventIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                {viewingEvent?.name}
              </Typography>
              {!viewingEvent?.all_day && (
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleOpenAddLessonDialog}
                  size="small"
                >
                  Dodaj zajęcia
                </Button>
              )}
            </Box>
          </DialogTitle>
          <DialogContent>
            {viewingEvent && (
              <Box>
                {/* Event details */}
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Data i godzina rozpoczęcia</Typography>
                        <Typography variant="body1">{formatDate(viewingEvent.start_date)}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Data i godzina zakończenia</Typography>
                        <Typography variant="body1">{formatDate(viewingEvent.end_date)}</Typography>
                      </Grid>
                      {viewingEvent.description && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">Opis</Typography>
                          <Typography variant="body1">{viewingEvent.description}</Typography>
                        </Grid>
                      )}
                      <Grid item xs={6}>
                        <Chip 
                          label={viewingEvent.active ? 'Aktywny' : 'Nieaktywny'} 
                          color={viewingEvent.active ? 'success' : 'error'} 
                          size="small" 
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Chip 
                          label={viewingEvent.show_commercial ? 'Publiczny' : 'Ukryty'} 
                          color={viewingEvent.show_commercial ? 'info' : 'default'} 
                          size="small" 
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Lessons list */}
                <Typography variant="h6" gutterBottom>
                  <CalendarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Zajęcia w evencie ({viewingEvent.lessons?.length || 0})
                </Typography>

                {viewingEvent.all_day ? (
                  <Alert severity="info">
                    Event całodniowy — zajmuje wybrane dni w całości i nie zawiera zajęć.
                  </Alert>
                ) : viewingEvent.lessons && viewingEvent.lessons.length > 0 ? (
                  <Paper sx={{ maxHeight: 300, overflow: 'auto' }}>
                    <List dense>
                      {viewingEvent.lessons.map((lesson) => (
                        <ListItem key={lesson.id}>
                          <ListItemText
                            primary={lesson.lesson_type_name || `Zajęcia #${lesson.id}`}
                            secondary={formatLessonDate(lesson)}
                          />
                          <ListItemSecondaryAction>
                            <IconButton 
                              edge="end" 
                              onClick={() => handleRemoveLesson(lesson.id)}
                              color="error"
                              size="small"
                            >
                              <RemoveIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                ) : (
                  <Alert severity="info">
                    Brak zajęć przypisanych do tego eventu. Użyj przycisku "Dodaj zajęcia" aby dodać.
                  </Alert>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewDialogOpen(false)}>Zamknij</Button>
          </DialogActions>
        </Dialog>

        {/* Add Lesson Dialog */}
        <Dialog open={addLessonDialogOpen} onClose={() => setAddLessonDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Dodaj zajęcia do eventu</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              {viewingEvent?.start_date && viewingEvent?.end_date ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Zakres dat eventu: {formatDate(viewingEvent.start_date)} - {formatDate(viewingEvent.end_date)}. Możesz zmienić zakres dat poniżej.
                </Alert>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Event nie ma jeszcze przypisanych zajęć. Wybierz zakres dat poniżej, aby wyszukać zajęcia.
                </Alert>
              )}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="Data od"
                    value={lessonDateRange.start}
                    onChange={(v) => setLessonDateRange(prev => ({ ...prev, start: v }))}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="Data do"
                    value={lessonDateRange.end}
                    onChange={(v) => setLessonDateRange(prev => ({ ...prev, end: v }))}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Typ zajęć</InputLabel>
                    <Select
                      value={selectedLessonType}
                      label="Typ zajęć"
                      onChange={(e) => setSelectedLessonType(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Wszystkie typy</em>
                      </MenuItem>
                      {lessonTypes.map((type) => (
                        <MenuItem key={type.id} value={type.id}>
                          {type.friendly_name || type.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {loadingLessons ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : (
                <Paper sx={{ maxHeight: 400, overflow: 'auto' }}>
                  <List dense>
                    {filteredLessons.length === 0 ? (
                      <ListItem>
                        <ListItemText primary="Brak dostępnych zajęć w wybranym zakresie dat" />
                      </ListItem>
                    ) : (
                      filteredLessons.map((lesson) => {
                        const typeName = lesson.lesson_type_name || lesson.lesson_type?.name || getLessonTypeName(lesson.lesson_type_id);
                        return (
                        <ListItem key={lesson.id}>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <span>{typeName || `Zajęcia #${lesson.id}`}</span>
                                {typeName && (
                                  <Chip 
                                    label={typeName} 
                                    size="small" 
                                    variant="outlined"
                                    color="primary"
                                  />
                                )}
                              </Box>
                            }
                            secondary={formatLessonDate(lesson)}
                          />
                          <ListItemSecondaryAction>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleAddLesson(lesson.id)}
                              startIcon={<AddIcon />}
                            >
                              Dodaj
                            </Button>
                          </ListItemSecondaryAction>
                        </ListItem>
                        );
                      })
                    )}
                  </List>
                </Paper>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddLessonDialogOpen(false)}>Zamknij</Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Potwierdź usunięcie</DialogTitle>
          <DialogContent>
            <Typography>
              Czy na pewno chcesz usunąć event "{deletingEvent?.name}"?
            </Typography>
            <Alert severity="warning" sx={{ mt: 2 }}>
              Ta operacja jest nieodwracalna. Zajęcia przypisane do eventu zostaną od niego odłączone.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} disabled={loading}>
              Anuluj
            </Button>
            <Button
              color="error"
              onClick={handleConfirmDelete}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? 'Usuwanie...' : 'Usuń'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default EventManagement;
